/** @jsxImportSource @emotion/react */
import { theme } from '@app/styles/theme'
import { css } from '@emotion/react'
import { Box, Typography } from '@mui/material'
import { ReactNode } from 'react'

type CalloutVariant = 'info' | 'warning' | 'success' | 'neutral' | 'error'

type CalloutBlockProps = {
  title?: string
  children: ReactNode
  variant?: CalloutVariant
}

type VariantStyles = {
  bg: string
  color: string
  border: string
}

const getVariantStyles = (variant: CalloutVariant): VariantStyles => {
  switch (variant) {
    case 'warning':
      return {
        bg: theme.palette.orange['100'] || '',
        color: theme.palette.text.primary,
        border: theme.palette.orange.main,
      }

    case 'success':
      return {
        bg: theme.palette.success.light,
        color: theme.palette.success.contrastText,
        border: theme.palette.success.main,
      }

    case 'error':
      return {
        bg: theme.palette.error.light,
        color: theme.palette.error.contrastText,
        border: theme.palette.error.main,
      }

    case 'neutral':
      return {
        bg: theme.palette.background.default,
        color: theme.palette.text.primary,
        border: theme.palette.neutral.light,
      }

    case 'info':
    default:
      return {
        bg: theme.palette.secondary['100'] || '',
        color: theme.palette.text.primary,
        border: theme.palette.secondary.main,
      }
  }
}

const containerStyles = (styles: VariantStyles) => css`
  background-color: ${styles.bg};
  color: ${styles.color};
  border-left: 4px solid ${styles.border};
  border-radius: ${theme.shape.borderRadius}px;
  padding: ${theme.spacing(1.5)} ${theme.spacing(2)};
  margin-bottom: ${theme.spacing(5)};
`

const titleStyles = css`
  font-weight: 600;
  margin-bottom: ${theme.spacing(0.5)};
`

const bodyStyles = css`
  color: inherit;
`

const CalloutBlock = ({ title, children, variant = 'info' }: CalloutBlockProps) => {
  const styles = getVariantStyles(variant)

  return (
    <Box css={containerStyles(styles)}>
      {title && (
        <Typography variant="subtitle2" css={titleStyles}>
          {title}
        </Typography>
      )}

      <Typography variant="body2" css={bodyStyles}>
        {children}
      </Typography>
    </Box>
  )
}

export default CalloutBlock
